import React, { useState, useEffect, useCallback } from "react";
import {
  PageHeader, KPIGrid, StandardModal, FilterPills, Alert, Btn, FormField,
  inputStyle, selectStyle, textareaStyle,
  colors, spacing, typography, radius, shadows,
} from "../design-system";

const BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const API = `${BASE}/accreditation-certificates`;
const BACKEND_ORIGIN = BASE.replace(/\/api$/, "");

function EntityTypeBadge({ type }) {
  const isCB = type === "cb";
  return (
    <span style={{
      background: isCB ? colors.primaryLight : "#f3e8ff",
      color:      isCB ? colors.primary      : colors.purple,
      padding: `2px 10px`, borderRadius: radius.pill,
      fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.semibold,
    }}>
      {isCB ? "Cert Body" : "Auditor"}
    </span>
  );
}

function CertCard({ cert, onDelete, onRevoke, isSuperAdmin, backendOrigin }) {
  const isRevoked  = !!cert.revoked_at;
  const isExpired  = !isRevoked && cert.expires_at && new Date(cert.expires_at) < new Date();
  const daysLeft   = !isRevoked && cert.expires_at
    ? Math.ceil((new Date(cert.expires_at) - new Date()) / (1000 * 86400))
    : null;

  const accentColor = isRevoked ? "#6b7280" : isExpired ? colors.danger : colors.primary;

  return (
    <div style={{
      background: colors.surface, border: `1px solid ${colors.border}`,
      borderRadius: radius.lg, padding: spacing.lg,
      position: "relative", overflow: "hidden",
      boxShadow: shadows.cardShadow,
    }}>
      {/* Top accent stripe */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accentColor }} />

      {/* Entity type + status row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: spacing.sm }}>
        <EntityTypeBadge type={cert.entity_type} />
        <span style={{
          background: isRevoked ? "#f3f4f6" : isExpired ? "#fee2e2" : "#dcfce7",
          color:      isRevoked ? "#6b7280"  : isExpired ? colors.danger : colors.success,
          padding: `2px 10px`, borderRadius: radius.pill,
          fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.semibold,
        }}>
          {isRevoked ? "Revoked" : isExpired ? "Expired" : "Active"}
        </span>
      </div>

      {/* Entity name + cert number */}
      <h3 style={{ margin: `0 0 ${spacing.xs}`, fontSize: typography.fontSizes.lg, color: colors.textPrimary }}>
        {cert.entity_name || `Entity #${cert.entity_id}`}
      </h3>
      <div style={{ fontFamily: "monospace", fontSize: typography.fontSizes.sm, color: colors.textMuted, marginBottom: spacing.md }}>
        {cert.certificate_number}
      </div>

      {/* Scope */}
      <div style={{
        fontSize: typography.fontSizes.sm, color: colors.textSecondary,
        background: colors.background, borderRadius: radius.md,
        padding: `${spacing.sm} ${spacing.md}`, marginBottom: spacing.md,
      }}>
        <strong>Scope:</strong> {cert.scope_summary || "—"}
      </div>

      {/* Dates */}
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
        <span>
          Issued: <strong style={{ color: colors.textPrimary }}>
            {cert.issued_at ? new Date(cert.issued_at).toLocaleDateString() : "—"}
          </strong>
        </span>
        <span>
          Expires: <strong style={{ color: isExpired ? colors.danger : (daysLeft !== null && daysLeft <= 90 ? colors.warning : colors.textPrimary) }}>
            {cert.expires_at ? new Date(cert.expires_at).toLocaleDateString() : "No expiry"}
          </strong>
        </span>
      </div>

      {/* Expiry warning */}
      {daysLeft !== null && !isExpired && daysLeft <= 90 && (
        <div style={{ marginTop: spacing.sm, fontSize: typography.fontSizes.xs, color: colors.warning, fontWeight: typography.fontWeights.semibold }}>
          Expires in {daysLeft} day{daysLeft !== 1 ? "s" : ""}
        </div>
      )}

      {/* Revocation note */}
      {isRevoked && (
        <div style={{ marginTop: spacing.sm, fontSize: typography.fontSizes.xs, color: "#6b7280" }}>
          Revoked {new Date(cert.revoked_at).toLocaleDateString()}
          {cert.revocation_reason ? ` — ${cert.revocation_reason}` : ""}
        </div>
      )}

      {/* Actions */}
      <div style={{ marginTop: spacing.md, display: "flex", gap: spacing.sm }}>
        {cert.certificate_url && (
          <a
            href={cert.certificate_url.startsWith("http") ? cert.certificate_url : `${backendOrigin}${cert.certificate_url}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1, display: "block", textAlign: "center",
              background: isRevoked ? "#9ca3af" : colors.primary, color: "#fff",
              padding: spacing.sm, borderRadius: radius.md,
              fontSize: typography.fontSizes.sm, textDecoration: "none",
              fontWeight: typography.fontWeights.semibold,
            }}
          >
            View Certificate
          </a>
        )}
        {isSuperAdmin && !isRevoked && (
          <button
            onClick={() => onRevoke(cert)}
            style={{
              background: colors.warning, color: "#fff", border: "none",
              borderRadius: radius.md, padding: `${spacing.sm} ${spacing.md}`,
              fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold,
              cursor: "pointer",
            }}
          >
            Revoke
          </button>
        )}
        {isSuperAdmin && (
          <button
            onClick={() => onDelete(cert.id)}
            style={{
              background: "transparent", color: colors.danger, border: `1px solid ${colors.danger}`,
              borderRadius: radius.md, padding: `${spacing.sm} ${spacing.md}`,
              fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold,
              cursor: "pointer",
            }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

export default function AccreditationCertificates() {
  const [certs, setCerts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch]         = useState("");
  const [showGenerate, setShowGenerate] = useState(false);
  const [genForm, setGenForm] = useState({
    entity_type: "cb", entity_id: "", entity_name: "", scope_summary: "", expires_at: "",
  });
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState("");
  const [msgType, setMsgType] = useState("success");
  const [certBodies, setCertBodies] = useState([]);
  const [auditors, setAuditors]     = useState([]);
  const [revokeTarget, setRevokeTarget] = useState(null);   // cert object being revoked
  const [revokeReason, setRevokeReason] = useState("");

  const token    = localStorage.getItem("token");
  const userRole = JSON.parse(localStorage.getItem("user") || "{}").role;
  const isSuperAdmin = userRole === "super_admin";
  const headers  = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const fetchCerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.set("entity_type", filterType);
      const res = await fetch(`${API}/?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to load certificates");
      const data = await res.json();
      setCerts(Array.isArray(data) ? data : []);
    } catch {
      setCerts([]);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { fetchCerts(); }, [fetchCerts]);

  useEffect(() => {
    fetch(`${BASE}/cert-bodies/`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCertBodies(Array.isArray(d) ? d : Array.isArray(d.certification_bodies) ? d.certification_bodies : []))
      .catch(() => {});
    fetch(`${BASE}/auditors/`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setAuditors(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(""), 3500); };

  async function handleDelete(certId) {
    if (!window.confirm("Permanently delete this certificate? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/${certId}/`, { method: "DELETE", headers });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Certificate deleted.");
      fetchCerts();
    } catch (e) { flash("Error: " + e.message, "error"); }
  }

  async function handleRevoke(e) {
    e.preventDefault();
    if (!revokeTarget) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/${revokeTarget.id}/revoke`, {
        method: "POST", headers,
        body: JSON.stringify({ reason: revokeReason || null }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || "Failed to revoke certificate");
      }
      flash(`Certificate ${revokeTarget.certificate_number} revoked.`);
      setRevokeTarget(null);
      setRevokeReason("");
      fetchCerts();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleGenerate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/generate`, {
        method: "POST", headers,
        body: JSON.stringify({
          entity_type:   genForm.entity_type,
          entity_id:     parseInt(genForm.entity_id),
          entity_name:   genForm.entity_name,
          scope_summary: genForm.scope_summary,
          expires_at:    genForm.expires_at || null,
        }),
      });
      if (!res.ok) {
        let detail = "Failed to generate certificate";
        try { const d = await res.json(); detail = d.detail || detail; } catch {}
        throw new Error(detail);
      }
      const created = await res.json();
      flash(`Certificate ${created.certificate_number} generated successfully.`);
      setShowGenerate(false);
      setGenForm({ entity_type: "cb", entity_id: "", entity_name: "", scope_summary: "", expires_at: "" });
      fetchCerts();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  const filtered = certs.filter(c =>
    search === "" ||
    (c.entity_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.certificate_number || "").toLowerCase().includes(search.toLowerCase())
  );

  const revokedCount = certs.filter(c => !!c.revoked_at).length;
  const activeCerts  = certs.filter(c => !c.revoked_at);
  const expiredCount = activeCerts.filter(c => c.expires_at && new Date(c.expires_at) < new Date()).length;
  const expiringSoon = activeCerts.filter(c => {
    if (!c.expires_at) return false;
    const d = (new Date(c.expires_at) - new Date()) / (1000 * 86400);
    return d > 0 && d <= 90;
  }).length;

  const kpis = [
    { label: "Active",             value: activeCerts.length - expiredCount, icon: "🎖️", color: colors.primary },
    { label: "Expiring (90 days)", value: expiringSoon,                      icon: "⚠️", color: expiringSoon > 0 ? colors.warning : colors.success },
    { label: "Expired",            value: expiredCount,                      icon: "🔴", color: expiredCount > 0 ? colors.danger : colors.textMuted },
    { label: "Revoked",            value: revokedCount,                      icon: "🚫", color: revokedCount > 0 ? "#6b7280" : colors.textMuted },
  ];

  const typeOptions = [
    { value: "all",     label: "All Types" },
    { value: "cb",      label: "Cert Bodies" },
    { value: "auditor", label: "Auditors" },
  ];

  return (
    <div>
      <PageHeader
        title="Accreditation Certificates"
        subtitle="Generate and manage ICSC accreditation certificates for CBs and Auditors."
        actions={isSuperAdmin && <Btn onClick={() => setShowGenerate(true)}>+ Generate Certificate</Btn>}
      />

      <KPIGrid kpis={kpis} columns={4} />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg("")} />}

      {/* Filters + Search */}
      <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.lg, flexWrap: "wrap", alignItems: "center" }}>
        <FilterPills options={typeOptions} value={filterType} onChange={setFilterType} />
        <input
          type="text"
          placeholder="Search by name or cert number..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, width: 280 }}
        />
      </div>

      {/* Certificate grid */}
      {loading ? (
        <div style={{ textAlign: "center", padding: spacing.xl, color: colors.textMuted }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: spacing.xl, color: colors.textMuted }}>No certificates found.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: spacing.lg }}>
          {filtered.map(cert => <CertCard key={cert.id} cert={cert} onDelete={handleDelete} onRevoke={setRevokeTarget} isSuperAdmin={isSuperAdmin} backendOrigin={BACKEND_ORIGIN} />)}
        </div>
      )}

      {/* Revoke Certificate Modal */}
      {revokeTarget && (
        <StandardModal
          title="Revoke Certificate"
          onClose={() => { setRevokeTarget(null); setRevokeReason(""); }}
          width={480}
          danger
          footer={
            <>
              <Btn variant="secondary" onClick={() => { setRevokeTarget(null); setRevokeReason(""); }}>Cancel</Btn>
              <Btn variant="danger" type="submit" form="revoke-cert-form" loading={saving}>Revoke Certificate</Btn>
            </>
          }
        >
          <form id="revoke-cert-form" onSubmit={handleRevoke}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <Alert type="warning" message={`This will revoke certificate ${revokeTarget.certificate_number} for ${revokeTarget.entity_name}. A new certificate can be issued afterwards.`} />
              <FormField label="Reason for revocation (optional)">
                <input
                  value={revokeReason}
                  onChange={e => setRevokeReason(e.target.value)}
                  placeholder="e.g. Scope change, renewal, administrative correction..."
                  style={inputStyle}
                />
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}

      {/* Generate Certificate Modal */}
      {showGenerate && (
        <StandardModal
          title="Generate Accreditation Certificate"
          onClose={() => setShowGenerate(false)}
          width={560}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowGenerate(false)}>Cancel</Btn>
              <Btn type="submit" form="gen-cert-form" loading={saving}>Generate Certificate</Btn>
            </>
          }
        >
          <form id="gen-cert-form" onSubmit={handleGenerate}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <FormField label="Certificate Type" required>
                <select
                  value={genForm.entity_type}
                  onChange={e => setGenForm(f => ({ ...f, entity_type: e.target.value, entity_id: "", entity_name: "" }))}
                  style={selectStyle}
                >
                  <option value="cb">Certification Body</option>
                  <option value="auditor">Auditor</option>
                </select>
              </FormField>

              <FormField label={genForm.entity_type === "cb" ? "Certification Body" : "Auditor"} required>
                <select
                  required
                  value={genForm.entity_id}
                  onChange={e => {
                    const id = e.target.value;
                    let name = "";
                    if (genForm.entity_type === "cb") {
                      const cb = certBodies.find(c => String(c.id) === id);
                      name = cb ? cb.name : "";
                    } else {
                      const a = auditors.find(a => String(a.id) === id);
                      name = a ? `${a.first_name} ${a.last_name}` : "";
                    }
                    setGenForm(f => ({ ...f, entity_id: id, entity_name: name }));
                  }}
                  style={selectStyle}
                >
                  <option value="">Select {genForm.entity_type === "cb" ? "a certification body" : "an auditor"}...</option>
                  {genForm.entity_type === "cb"
                    ? (certBodies || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                    : (auditors || []).map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)
                  }
                </select>
              </FormField>

              <FormField label="Name on Certificate" required>
                <input
                  required
                  value={genForm.entity_name}
                  onChange={e => setGenForm(f => ({ ...f, entity_name: e.target.value }))}
                  placeholder="Auto-filled from selection, or override here"
                  style={inputStyle}
                />
              </FormField>

              <FormField label="Scope Summary" required>
                <textarea
                  required
                  value={genForm.scope_summary}
                  onChange={e => setGenForm(f => ({ ...f, scope_summary: e.target.value }))}
                  rows={4}
                  placeholder="e.g. ISO 9001:2015 Quality Management Systems, ISO 14001:2015 Environmental Management..."
                  style={textareaStyle}
                />
              </FormField>

              <FormField label="Expiry Date (optional)">
                <input
                  type="datetime-local"
                  value={genForm.expires_at}
                  onChange={e => setGenForm(f => ({ ...f, expires_at: e.target.value }))}
                  style={inputStyle}
                />
              </FormField>

              <div style={{
                background: "#f0f9ff", border: `1px solid #bae6fd`,
                borderRadius: radius.md, padding: `${spacing.sm} ${spacing.md}`,
                fontSize: typography.fontSizes.sm, color: "#0369a1",
              }}>
                An HTML certificate will be generated with a print-to-PDF button. The certificate number will be auto-assigned.
              </div>
            </div>
          </form>
        </StandardModal>
      )}
    </div>
  );
}
